import styles from './DynamicFormBuilder.module.css';
import { panelForType } from './fieldTypes/index.js';
import OptionsEditor from './OptionsEditor.jsx';
import VisibilityEditor, { eligibleSourceKeys } from './VisibilityEditor.jsx';

const HELP_POSITIONS = ['above', 'below'];

const ENUM_TYPES = new Set(['radio', 'select', 'multi_select', 'multi_checkbox']);

// Markup widgets surfaced as first-class entries in the Type picker. They are
// not canonical field types — each serializes to a `markup` field carrying
// `config.widget`, the key the React renderer's widgetComponents dispatch on
// (and the shape DynamicForms::AlpacaImporter emits for legacy widgets).
const WIDGET_TYPE_OPTIONS = [
  { value: 'chem_doodle', label: 'Chemical structure — draw or edit a molecule', widget: 'chem_doodle' },
];

const BOOLEAN_ATTRS = [
  ['required', 'Required'],
  ['hidden', 'Hidden'],
  ['disabled', 'Disabled'],
  ['readonly', 'Readonly'],
  ['display_only', 'Display only'],
];

const FIELD_LABEL_CLASS = 'form-label small text-uppercase text-muted d-flex flex-column gap-1 mb-0';
const INPUT_CLASS = 'form-control form-control-sm';
const SELECT_CLASS = 'form-select form-select-sm';

export default function FieldEditor({ field, path, definition, typeCatalog, fieldErrors = [], onChange }) {
  if (!field) {
    return <p className="m-0 small text-muted fst-italic">Select a field on the left to edit it.</p>;
  }

  const setAttr = (key, value) => {
    onChange(path, (prev) => ({ ...prev, [key]: value }));
  };

  const setType = (nextType) => {
    const widgetOption = WIDGET_TYPE_OPTIONS.find((w) => w.value === nextType);
    if (widgetOption) {
      onChange(path, (prev) => ({ ...prev, type: 'markup', config: { widget: widgetOption.widget } }));
    } else {
      onChange(path, (prev) => ({ ...prev, type: nextType, config: {} }));
    }
  };

  const onConfigChange = (transform) => {
    onChange(path, (prev) => ({ ...prev, config: transform(prev?.config || {}) }));
  };

  const onOptionsChange = (nextOptions) => {
    onChange(path, (prev) => ({ ...prev, options: nextOptions }));
  };

  const onVisibilityChange = (nextRules) => {
    onChange(path, (prev) => ({ ...prev, visibility: nextRules }));
  };

  // A markup field carrying a known widget is shown in the picker as that widget
  // rather than as plain "markup", and its generic markup config (body/style) is
  // hidden since a widget doesn't use it.
  const widgetOption = field.type === 'markup' && field.config?.widget
    ? WIDGET_TYPE_OPTIONS.find((w) => w.widget === field.config.widget)
    : null;
  const selectedType = widgetOption ? widgetOption.value : field.type;

  const Panel = widgetOption ? null : panelForType(field.type);
  const isEnum = ENUM_TYPES.has(field.type);
  const candidateKeys = eligibleSourceKeys(definition, path);

  return (
    <div className="d-flex flex-column gap-3">
      {fieldErrors.length > 0 && (
        <ul className="alert alert-danger mb-0 ps-4" role="alert">
          {fieldErrors.map((message, i) => (
            <li key={i}>{message}</li>
          ))}
        </ul>
      )}
      <div className={styles.editorGrid}>
        <label className={FIELD_LABEL_CLASS}>
          <span>Key</span>
          <input
            type="text"
            value={field.key ?? ''}
            onChange={(e) => setAttr('key', e.target.value)}
            className={INPUT_CLASS}
          />
        </label>

        <label className={FIELD_LABEL_CLASS}>
          <span>Type</span>
          <select
            value={selectedType ?? ''}
            onChange={(e) => setType(e.target.value)}
            className={SELECT_CLASS}
          >
            {typeCatalog.map((entry) => (
              <option key={entry.type} value={entry.type}>{entry.label || entry.type}</option>
            ))}
            {WIDGET_TYPE_OPTIONS.map((w) => (
              <option key={w.value} value={w.value}>{w.label}</option>
            ))}
          </select>
        </label>

        <label className={FIELD_LABEL_CLASS}>
          <span>Label</span>
          <input
            type="text"
            value={field.label ?? ''}
            onChange={(e) => setAttr('label', e.target.value)}
            className={INPUT_CLASS}
          />
        </label>

        <label className={FIELD_LABEL_CLASS}>
          <span>Description</span>
          <textarea
            rows={2}
            value={field.description ?? ''}
            onChange={(e) => setAttr('description', e.target.value)}
            className={INPUT_CLASS}
          />
        </label>

        <label className={FIELD_LABEL_CLASS}>
          <span>Help text</span>
          <textarea
            rows={2}
            value={field.help_text ?? ''}
            onChange={(e) => setAttr('help_text', e.target.value)}
            className={INPUT_CLASS}
          />
        </label>

        <label className={FIELD_LABEL_CLASS}>
          <span>Help position</span>
          <select
            value={field.help_position ?? ''}
            onChange={(e) => setAttr('help_position', e.target.value || null)}
            className={SELECT_CLASS}
          >
            <option value="">(default)</option>
            {HELP_POSITIONS.map((pos) => (
              <option key={pos} value={pos}>{pos}</option>
            ))}
          </select>
        </label>

        <label className={FIELD_LABEL_CLASS}>
          <span>Default value</span>
          <input
            type="text"
            value={field.default_value ?? ''}
            onChange={(e) => setAttr('default_value', e.target.value === '' ? null : e.target.value)}
            className={INPUT_CLASS}
          />
        </label>
      </div>

      <fieldset className={`border rounded p-2 d-flex flex-wrap gap-2 ${styles.flagSet}`}>
        <legend className="form-text small text-uppercase text-muted px-1 float-none w-auto">Flags</legend>
        {BOOLEAN_ATTRS.map(([attr, label]) => (
          <div key={attr} className="col-6 col-sm-3">
            <div className="checkbox m-0">
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(field[attr])}
                  onChange={(e) => setAttr(attr, e.target.checked)}
                />
                <span>{label}</span>
              </label>
            </div>
          </div>
        ))}
        {/* A config key, not a field attribute — the renderer reads it off
            config.html_labels (as the Alpaca importer writes it). */}
        <div className="col-6 col-sm-3">
          <div className="checkbox m-0">
            <label>
              <input
                type="checkbox"
                checked={Boolean(field.config?.html_labels)}
                onChange={(e) =>
                  onConfigChange((config) => {
                    const next = { ...config };
                    if (e.target.checked) next.html_labels = true;
                    else delete next.html_labels;
                    return next;
                  })
                }
              />
              <span>HTML titles and captions</span>
            </label>
          </div>
        </div>
      </fieldset>

      {Panel && (
        <section className={`mt-2 pt-2 ${styles.subsection}`}>
          <h4 className="h6 mb-2">Type-specific config ({field.type})</h4>
          <Panel field={field} onConfigChange={onConfigChange} />
        </section>
      )}

      {isEnum && (
        <section className={`mt-2 pt-2 ${styles.subsection}`}>
          <h4 className="h6 mb-2">Options</h4>
          <OptionsEditor options={field.options || []} onChange={onOptionsChange} />
        </section>
      )}

      <section className={`mt-2 pt-2 ${styles.subsection}`}>
        <h4 className="h6 mb-2">Visibility</h4>
        <VisibilityEditor
          rules={field.visibility || []}
          candidateKeys={candidateKeys}
          onChange={onVisibilityChange}
        />
      </section>
    </div>
  );
}
